import * as _ from 'lodash';
import * as moment from 'moment';

import {
  BreakageTotals,
  PayFactorTotal,
  ValueError, PayFactor
} from '../../../../common/timecard.model';
import {GrossPay} from '../../../../common/htg-timecard.submodel';
import {ErrorType} from '../../../../common/timecard.enum';
import {Breakage, Breakages, ShiftDates} from '../../../../common/timecard.shifts.model';
import {StandardDateFormatString} from '../../../../common/date-format.constants';

export interface BreakageOverrideViewModel {
  breakages: ShiftBreakageOverride[];
  breakageTotals: BreakageOverrideTotals;
}

export interface ShiftBreakageOverride {
  shiftId: string;
  splitShiftOrdinal?: number;
  shiftDate: string;
  hoursPaid: number;
  hoursWorked: number;
  MP1Count: number;
  MP1Amount: number;
  MP2Count: number;
  MP2Amount: number;
  payFactors?: PayFactor[];
  totalAmount: number;
  originalHoursPaid?: number;
  originalTotalAmount?: number;
  color: string;
  borderBottom: string;
}
export interface BreakageOverrideTotals {
  hoursPaid: number;
  hoursWorked: number;
  MP1Count: number;
  MP1Amount: number;
  MP2Count: number;
  MP2Amount: number;
  payFactors?: PayFactorTotal[];
  grandTotal: number;
  originalHoursPaid?: number;
  originalGrandTotal?: number;
}

export interface PayHourTotalsError {
  errorMessage: string;
  isRevisionTotalLess: boolean;
}

export function IsShiftPayfactorsUnderPaid(payFactors: PayFactor[]): boolean {
  const sumOfOriginalDuration = _.sumBy(payFactors, (pf: PayFactor) => pf?.originalHours ?? pf?.duration?.value);
  const sumOfDuration = _.sumBy(payFactors, (pf: PayFactor) => pf?.duration?.value);
  return (sumOfOriginalDuration - sumOfDuration) > 0;
}

export function IsShiftPayfactorsOverPaid(payFactors: PayFactor[]): boolean {
  const sumOfOriginalDuration = _.sumBy(payFactors, (pf: PayFactor) => pf?.originalHours ?? pf?.duration?.value);
  const sumOfDuration = _.sumBy(payFactors, (pf: PayFactor) => pf?.duration?.value);
  return (sumOfOriginalDuration - sumOfDuration) < 0;
}

export function GetPayHourTotalsError(breakages: ShiftBreakageOverride[]): PayHourTotalsError {

  const underpaidShifts: ShiftBreakageOverride[] = _.filter(breakages, ({payFactors}: ShiftBreakageOverride) => IsShiftPayfactorsUnderPaid(payFactors));
  if (!_.isEmpty(underpaidShifts)) {
    const underpaidPayFactors: PayFactor[][] = _.map(underpaidShifts, 'payFactors');
    const sumOfOriginalDurationsInPayfactors = _.reduce(underpaidPayFactors, (sum: number, payFactors: PayFactor[]) => {
      const sumOfOriginalDurationsInShift = _.sumBy(payFactors, (pf: PayFactor) => pf?.originalHours ?? pf.duration.value);
      return  sum + sumOfOriginalDurationsInShift;
    }, 0);
    const sumOfDurationsInShifts = _.reduce(underpaidPayFactors, (sum: number, pfTotal: PayFactor[]) => {
      const sumOfDurationsInShift = _.sumBy(pfTotal, (pf: PayFactor) =>  pf.duration.value);
      return  sum + sumOfDurationsInShift;
    }, 0);
    const difference = (sumOfOriginalDurationsInPayfactors - sumOfDurationsInShifts).toFixed(2);
    return {
      errorMessage: `You have ${difference} hours left to allocate.`,
      isRevisionTotalLess: true
    };
  }
  const overPaidShifts: ShiftBreakageOverride[] = _.filter(breakages, ({payFactors}: ShiftBreakageOverride) => IsShiftPayfactorsOverPaid(payFactors));
  if (!_.isEmpty(overPaidShifts)) {
    const overPaidPayFactors: PayFactor[][] = _.map(overPaidShifts, 'payFactors');
    const sumOfOriginalDurationsInAllPayfactors = _.reduce(overPaidPayFactors, (sum: number, payFactors: PayFactor[]) => {
      const sumOfOriginalDurationsInShift = _.sumBy(payFactors, (pf: PayFactor) => pf?.originalHours ?? pf.duration.value);
      return  sum + sumOfOriginalDurationsInShift;
    }, 0);
    const sumOfDurationsInAllBreakages = _.reduce(overPaidPayFactors, (sum: number, pfTotal: PayFactor[]) => {
      const sumOfDurationsInShift = _.sumBy(pfTotal, (pf: PayFactor) =>  pf.duration.value);
      return  sum + sumOfDurationsInShift;
    }, 0);
    const difference = (sumOfDurationsInAllBreakages - sumOfOriginalDurationsInAllPayfactors).toFixed(2);
    return {
      errorMessage: `You have added ${difference} hours.`,
      isRevisionTotalLess: false
    };
  }
}

export function MapToBreakageOverrideViewModel(
  shiftDates: ShiftDates,
  orderedShiftIds: { [key: string]: string }[],
  payTimeBreakages: Breakages,
  rawBreakages: GrossPay
): BreakageOverrideViewModel {
  const breakagesView = _.map(orderedShiftIds, ({shiftId, color, borderBottom}) => {
    const {
      hoursPaid,
      hoursWorked,
      MP1Count,
      MP1Amount,
      MP2Count,
      MP2Amount,
      payFactors,
      totalAmount,
      originalTotalAmount,
      originalHoursPaid
    } = payTimeBreakages[shiftId];
    return {
      shiftId,
      shiftDate: shiftDates[shiftId].date,
      splitShiftOrdinal: _.get(shiftDates[shiftId], 'splitShiftOrdinal', 0),
      hoursPaid,
      hoursWorked,
      MP1Count,
      MP1Amount,
      MP2Count,
      MP2Amount,
      payFactors: _.map(payFactors, ({payFactor, originalHours, rate, duration, payCode, ...etc}) => {
        const breakageSummary = _.find(rawBreakages.breakageSummaryList, ({shiftDate, payFactor: breakagePayFactor,
                                                                            splitShiftSeqNumber, paycodeObject: {paycode}}) =>
          _.isEqual(payFactor, breakagePayFactor) &&
          _.isEqual(payCode, paycode) &&
          _.isEqual(shiftDates[shiftId].date, moment.utc(shiftDate).format(StandardDateFormatString)) &&
          _.isEqual(_.get(shiftDates[shiftId], 'splitShiftOrdinal', 0), splitShiftSeqNumber));
        if (_.isUndefined(breakageSummary)) {
          return {
            ...etc,
            payFactor,
            originalHours,
            rate,
            duration,
            payCode
          };
        }

        const durationValue: ValueError<number> = !_.isUndefined(originalHours)
        && !_.isEqual(originalHours, breakageSummary?.duration)
          ? { errorType: ErrorType.SOFT_ERROR, value: breakageSummary?.duration }
          : { value: breakageSummary?.duration };

        return {
          payFactor,
          originalHours : originalHours ?? duration.value,
          rate,
          payCode: breakageSummary?.paycodeObject?.paycode ?? '',
          duration: durationValue
        };
      }),
      totalAmount,
      originalHoursPaid: originalHoursPaid ?? hoursPaid,
      originalTotalAmount: originalTotalAmount ?? totalAmount,
      color,
      borderBottom
    };
  });
  const breakageTotal = MapToBreakageTotals(breakagesView);
  return {
    breakages: breakagesView,
    breakageTotals: breakageTotal
  };
}
export function MapToBreakageTotals(breakagesView: ShiftBreakageOverride[] | Breakages): BreakageTotals {
  return {
    hoursPaid: _.chain(breakagesView).map(({hoursPaid}) => hoursPaid).sum().round(1).value(),
    hoursWorked: _.chain(breakagesView).map(({hoursWorked}) => hoursWorked).sum().round(1).value(),
    MP1Count: _.chain(breakagesView).map(({MP1Count}) => MP1Count).sum().round(1).value(),
    MP1Amount: _.chain(breakagesView).map(({MP1Amount}) => MP1Amount).sum().round(4).value(),
    MP2Count: _.chain(breakagesView).map(({MP2Count}) => MP2Count).sum().round(1).value(),
    MP2Amount: _.chain(breakagesView).map(({MP2Amount}) => MP2Amount).sum().round(4).value(),
    payFactors:  _
    .chain(breakagesView)
    .map((breakages: ShiftBreakageOverride|Breakage) => breakages?.payFactors)
    .flatten()
    .groupBy((breakage) => breakage.payFactor)
    .map((breakageGroupByPayFactor, payFactor) => ({
      payFactor: _.toNumber(payFactor),
      originalTotal: _.round(_.sumBy(breakageGroupByPayFactor, 'originalHours'), 1),
      total: _.round(_.sumBy(breakageGroupByPayFactor, 'duration.value'), 1),
    }))
    .sortBy('payFactor')
    .value(),
    grandTotal: _.chain(breakagesView).map(({totalAmount}) => totalAmount).sum().round(2).value(),
    originalHoursPaid: _.chain(breakagesView).map(({originalHoursPaid, hoursPaid}) => originalHoursPaid ??  hoursPaid).sum().round(1).value(),
    originalGrandTotal: _.chain(breakagesView).map(({originalTotalAmount, totalAmount}) => originalTotalAmount ?? totalAmount).sum().round(2).value(),
  };
}

export function ResetBreakageOverrideView(breakages: ShiftBreakageOverride[]): BreakageOverrideViewModel {
  const updatedBreakages = _.map(breakages, (breakage: ShiftBreakageOverride) => {
    const updatedPayFactors = _.map(breakage.payFactors, (payFactor) => {
      if (_.isEqual(payFactor.originalHours, payFactor.duration.value)) {
        return payFactor;
      }
      return {
        ...payFactor,
        duration: {value: payFactor.originalHours},
      };
    });
    const payValuesSum: number = _.sumBy(updatedPayFactors, (payFactor: PayFactor) =>
      ((_.toNumber(payFactor.rate) * _.toNumber(payFactor.duration.value || 0) * _.toNumber(payFactor.payFactor))));
    const totalAmount = _.round(payValuesSum + _.toNumber(breakage.MP1Amount) + _.toNumber(breakage.MP2Amount), 4);
    return {
      ...breakage,
      totalAmount,
      hoursPaid: breakage.originalHoursPaid ?? breakage.hoursPaid,
      payFactors: updatedPayFactors
    };
  });

  const breakageTotals = MapToBreakageTotals(updatedBreakages);
  return {breakages: updatedBreakages, breakageTotals};
}
